// src/pages/portal/PortalReset.jsx
//
// Acá aterriza el link de "¿Olvidaste tu contraseña?" del correo. Supabase
// procesa el token del link (detectSessionInUrl) y deja una sesión de
// recuperación activa; con ella el usuario define su contraseña nueva.
//
// Si alguien entra directo sin link (sin sesión), se le explica y se lo manda
// al login.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

export default function PortalResetPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(null); // null=verificando · true=hay sesión · false=no
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    // El token del link tarda un instante en procesarse: escuchar el evento y,
    // por las dudas, consultar la sesión un momento después.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (!alive) return;
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true);
    });
    const t = setTimeout(async () => {
      if (!alive) return;
      const { data } = await supabase.auth.getSession();
      setReady((prev) => (prev === null ? !!data?.session : prev));
    }, 1200);
    return () => { alive = false; clearTimeout(t); sub?.subscription?.unsubscribe?.(); };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (pass.length < 8) { setMsg({ type: 'error', text: 'La contraseña debe tener al menos 8 caracteres.' }); return; }
    if (pass !== pass2) { setMsg({ type: 'error', text: 'Las contraseñas no coinciden.' }); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pass });
      if (error) throw error;
      setMsg({ type: 'ok', text: 'Contraseña actualizada. Entrando al portal…' });
      setTimeout(() => navigate('/portal', { replace: true }), 1200);
    } catch (err) {
      const t = String(err?.message || err);
      setMsg({
        type: 'error',
        text: /different from the old/i.test(t)
          ? 'La contraseña nueva no puede ser igual a la anterior.'
          : `No se pudo actualizar: ${t}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-xl bg-slate-800/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50';

  return (
    <div className="min-h-screen bg-[#0b0f14] text-white px-4 py-10">
      <div className="mx-auto w-full max-w-sm">
        <div className="text-center mb-7">
          <div className="text-3xl font-bold tracking-tight">Calins</div>
          <div className="text-sm text-slate-400 mt-1">Portal de clientes · Nueva contraseña</div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          {ready === null ? (
            <div className="text-sm text-slate-400 text-center py-4">Verificando el link…</div>
          ) : ready === false ? (
            <div className="space-y-3 text-center py-2">
              <div className="text-3xl">✉️</div>
              <p className="text-sm text-slate-300">
                Este enlace no es válido o ya venció. Pedí uno nuevo desde
                “¿Olvidaste tu contraseña?” en el ingreso.
              </p>
              <button
                onClick={() => navigate('/portal/login')}
                className="w-full rounded-xl bg-sky-600 hover:bg-sky-500 transition px-4 py-3 font-semibold"
              >
                Ir al ingreso
              </button>
            </div>
          ) : (
            <>
              {msg && (
                <div className={`mb-4 rounded-lg border px-3 py-2.5 text-sm ${msg.type === 'ok' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-rose-500/30 bg-rose-500/10 text-rose-200'}`}>
                  {msg.text}
                </div>
              )}
              <form onSubmit={handleSave} className="space-y-3">
                <input type="password" required autoComplete="new-password" placeholder="Contraseña nueva (mín. 8)" value={pass} onChange={(e) => setPass(e.target.value)} className={inputCls} />
                <input type="password" required autoComplete="new-password" placeholder="Repetí la contraseña" value={pass2} onChange={(e) => setPass2(e.target.value)} className={inputCls} />
                <button type="submit" disabled={busy} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 transition px-4 py-3 font-semibold disabled:opacity-60">
                  {busy ? 'Guardando…' : 'Guardar contraseña nueva'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
